import React, { useEffect, useMemo, useState } from 'react'
import { hoyLocal } from '../utils/fecha'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  CCol, CRow, CForm, CFormInput, CInputGroup, CInputGroupText, CFormLabel, CFormSelect, CButton, CAlert,
  CCard, CCardBody, CCardHeader,
  CTabs, CTabList, CTab, CTabPanel, CSpinner,
} from '@coreui/react'
import { api, currentUser } from '../api/client'
import { capitalizarPalabras } from '../utils/texto'
import { fmtMoneda, simboloMoneda } from '../utils/moneda'
import { numeroWhatsApp } from '../utils/whatsapp'
import { NumericFormat } from 'react-number-format'
import WwGrid, { WwColumn, WwRowAction } from '../components/ww/WwGrid'
import WwFilterBar from '../components/ww/WwFilterBar'
import WwSearchField from '../components/ww/WwSearchField'
import { usePendiente } from '../components/ww/pendiente'
import ModificarAbonoModal from '../components/abonos/ModificarAbonoModal'

// [RGM] 2026-10-03 (pedido Rafael: el .xlsx debe mostrar el TEXTO visible):
// mapas compartidos entre el render de la grilla y el valor plano del export.
const METODO_TXT: Record<number, string> = { 1: 'Efectivo', 2: 'Transferencia' }
const MODALIDAD_TXT: Record<number, string> = { 1: 'DIARIO', 7: 'SEMANAL', 15: 'QUINCENAL', 30: 'MENSUAL', 60: 'LIBRE' }

const columnas: WwColumn[] = [
  { key: 'abonoNro', title: 'Nro', type: 'number', link: true },
  { key: 'abonoFecha', title: 'Fecha', type: 'date' },
  { key: 'abonoHora', title: 'Hora' },
  { key: 'abonoTipoAbono', title: 'Tipo Abono' },
  { key: 'abonoValor', title: 'Valor', type: 'currency' },
  // [RGM] Método de pago del abono (abonoLibreValor3: 1=Efectivo,
  // 2=Transferencia — semántica RGM; otro → celda vacía).
  {
    key: 'abonoLibreValor3', title: 'Método',
    render: (row) => METODO_TXT[Number(row.abonoLibreValor3)] ?? '',
    exportValue: (row) => METODO_TXT[Number(row.abonoLibreValor3)] ?? '',
  },
  { key: 'prestamoNro', title: 'Prestamo Nro', type: 'number' },
  { key: 'prestamoFecha', title: 'Prestamo Fecha', type: 'date' },
  { key: 'prestamoSaldo', title: 'Prestamo Saldo', type: 'currency' },
  {
    key: 'prestamoMPago', title: 'Modalidad',
    // [RGM] 2026-09-17 (pedido Rafael: "Mpago en abonos [igual] como antes [en
    // prestamos]"): TEXTO de la modalidad (labels GX 1=DIARIO..60=LIBRE).
    render: (row) => MODALIDAD_TXT[Number(row.prestamoMPago)] ?? String(row.prestamoMPago ?? '').trim(),
    exportValue: (row) => MODALIDAD_TXT[Number(row.prestamoMPago)] ?? String(row.prestamoMPago ?? '').trim(),
  },
  { key: 'clienteCedula', title: 'Cédula', type: 'id' },
  { key: 'clienteNombre', title: 'Nombre' },
  { key: 'clienteCelular', title: 'Whatsapp' },
  { key: 'abonoProximoCobro', title: 'Proximo Cobro', type: 'date' },
  { key: 'abonoCreadoPor', title: 'Cobrador Usuario' },
  { key: 'prestamoCuota', title: 'Valor Cuota', type: 'currency' },
  { key: 'abonoLibreCaracter1', title: 'Observación' },
]

const tiposAbono = ['Prestamo', 'Atraso', 'Condena', 'PrestamoCondena', 'PagoCapital', 'Interes', 'AbonoCapital']

const hoy = () => hoyLocal()

const AbonosPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [rows, setRows] = useState<any[]>([])
  // ── Filtros rápidos: tipo de abono y cobrador (los más usados) ──
  const [filtroTipo, setFiltroTipo] = useState('')
  const [filtroCobrador, setFiltroCobrador] = useState('')
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const { aviso, setAviso } = usePendiente()
  // Préstamo elegido en el tab Abono Ágil
  const [prestamoSel, setPrestamoSel] = useState<any>(null)
  // Fila en edición (abonoww.cs:2896-2898 'DO UPDATE' → webobject abono Mode="UPD")
  const [editando, setEditando] = useState<any | null>(null)
  // Form del ALTA ÁGIL (diseño RGM): monto, método de pago, fecha, observaciones
  // y próximo cobro. El método de pago 1=Efectivo / 2=Transferencia es semántica
  // nueva RGM que viaja en AbonoLibreValor3 del abono (PrestamosController.cs:289-295).
  const [formAgil, setFormAgil] = useState({
    valor: '', metodoPago: '1', fecha: hoy(), observaciones: '', proximoCobro: '',
  })
  // Preview del abono del préstamo elegido — GET /prestamos/{nro}/abono-preview
  // (PrestamosController.cs AbonoPreview, misma lógica que
  // MovilController.cs:1077-1139): valorSugerido CON pico y proximoCobro
  // calculado. Se precargan en el form pero quedan EDITABLES.
  const [previewAgil, setPreviewAgil] = useState<any>(null)
  const [guardandoAgil, setGuardandoAgil] = useState(false)
  const [exitoAgil, setExitoAgil] = useState('')

  // Flujo 'Abonar' desde el grid de préstamos (PrestamosPage.tsx:312 →
  // /abonos?prestamo=N): preselecciona el préstamo en el tab Abono Ágil.
  useEffect(() => {
    const nro = searchParams.get('prestamo')
    if (nro) {
      api
        .get<any[]>(`/prestamos?empresa=${empresa}`)
        .then((lista) => {
          const p = (lista ?? []).find((x) => String(x.prestamoNro) === String(nro))
          // Mismo flujo que onSeleccionar: precarga el preview del abono.
          if (p) seleccionarPrestamo(p)
        })
        .catch(() => {})
      setSearchParams({}, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Modificar (abonoww.cs:2896-2898): selecciona la fila y abre el modal
  // compartido — el MISMO formulario que el detalle (AbonoViewPage). El alta
  // vive en el tab Abono Ágil.
  const abrirEditar = (fila: any) => {
    setEditando(fila)
  }

  // Registrar abono ágil — POST /prestamos/{nro}/abonos (PrestamosController.cs:263-307).
  // Campos del diseño RGM: valor, fecha, proximoCobro, abonoLibreCaracter1
  // (observaciones), metodoPago (1=Efectivo, 2=Transferencia → AbonoLibreValor3)
  // y abonoCuotas (flujo actual: 0, el BC clasifica el tipo automáticamente).
  const registrarAgil = async (e: React.FormEvent) => {
    e.preventDefault()
    // [RGM] 2026-10-14 (pedido Rafael): alerta cuando el abono SUPERA el
    // saldo pendiente — pudo digitarse mal (un cero de más). NO bloquea: en
    // el contrato GX abonar de más es legítimo (salda el préstamo y el
    // próximo cobro pasa a HOY — getproximocobroenabonodirecto.cs:400-403).
    const valorIngresado = Number(formAgil.valor)
    const saldoPendiente = Number(prestamoSel?.prestamoSaldo ?? 0)
    if (saldoPendiente > 0 && valorIngresado > saldoPendiente) {
      const continuar = window.confirm(
        `El abono ${fmtMoneda(valorIngresado)} supera el saldo pendiente ${fmtMoneda(saldoPendiente)}.\n\n¿Está seguro? Verifique el valor digitado.`,
      )
      if (!continuar) return
    }
    setExitoAgil('')
    setError('')
    setGuardandoAgil(true)
    try {
      await api.post(`/prestamos/${Number(prestamoSel.prestamoNro)}/abonos`, {
        prestamoNro: Number(prestamoSel.prestamoNro), empresaCodigo: empresa,
        valor: Number(formAgil.valor), fecha: formAgil.fecha,
        proximoCobro: formAgil.proximoCobro || undefined,
        abonoCuotas: 0,
        abonoLibreCaracter1: formAgil.observaciones || undefined,
        metodoPago: Number(formAgil.metodoPago),
      })
      setExitoAgil('Pago registrado exitosamente.')
      // Limpiar el form ágil; el préstamo se mantiene para cobros rápidos
      // consecutivos del mismo cliente.
      setFormAgil({ valor: '', metodoPago: '1', fecha: hoy(), observaciones: '', proximoCobro: '' })
      cargar()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setGuardandoAgil(false)
    }
  }

  const eliminar = (fila: any) => {
    // abonoww.cs:2904-2906 — DO DELETE: webobject abono Mode="DLT" con
    // AbonoNro. El backend replica el BC DLT (abono_bc.cs:1549-1594) que
    // revierte el préstamo/caja y borra el abono (BC000118).
    if (!window.confirm(`¿Eliminar el abono ${fila.abonoNro}?`)) return
    api.delete(`/abonos/${fila.abonoNro}?empresa=${empresa}`)
      .then(() => {
        setAviso('Abono eliminado.')
        cargar()
      })
      .catch((e: any) => setError(e.message))
  }

  // Filas filtradas por tipo/cobrador (client-side, patrón Préstamos)
  const visibleRows = useMemo(() => rows.filter((row) => {
    if (filtroTipo && String(row.abonoTipoAbono ?? '').trim() !== filtroTipo) return false
    if (filtroCobrador && String(row.abonoCreadoPor ?? '').trim() !== filtroCobrador) return false
    return true
  }), [rows, filtroTipo, filtroCobrador])

  // Tipos de abono: dominio GX fijo (constante del módulo, línea 34) —
  // no se deriva de rows. Cobradores: valores únicos de las filas.
  const cobradoresAbono = useMemo(() => [...new Set(rows.map((r) => String(r.abonoCreadoPor ?? '').trim()).filter(Boolean))], [rows])

  const chips = [
    filtroTipo ? { clave: 'tipo', label: `Tipo: ${filtroTipo}` } : null,
    filtroCobrador ? { clave: 'cobrador', label: `Cobrador: ${filtroCobrador}` } : null,
  ].filter(Boolean) as { clave: string; label: string }[]

  const limpiarFiltros = () => { setFiltroTipo(''); setFiltroCobrador('') }
  const quitarFiltro = (clave: string) => {
    if (clave === 'tipo') setFiltroTipo('')
    if (clave === 'cobrador') setFiltroCobrador('')
  }

  const cargar = () => {
    setCargando(true)
    api.get<any[]>(`/abonos?empresa=${empresa}`)
      .then(setRows)
      .catch((e: any) => setError(e.message))
      .finally(() => setCargando(false))
  }
  useEffect(cargar, [empresa])

  // Moneda: fuente única utils/moneda.ts (fmtMoneda — símbolo configurado por
  // empresa + máscara GX "$ Z,ZZZ,ZZZ,ZZ9.99", aticket.cs:222). [RGM] 2026-10-02 (F)

  const fmtFecha = (v: any) => {
    // Acepta "2026-01-08T00:00:00" o "2026-01-08" → dd/mm/yyyy (99/99/99 GX).
    const s = String(v ?? '')
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
    return m ? `${m[3]}/${m[2]}/${m[1]}` : ''
  }

  const fmtFechaValida = (v: any) => {
    // DateTime.MinValue del préstamo (sin próximo cobro asignado) → ''.
    const s = String(v ?? '')
    if (!s || s.startsWith('0001')) return ''
    return fmtFecha(s)
  }

  const buscarPrestamos = async (texto: string): Promise<any[]> => {
    const t = texto.trim().toLowerCase()
    const todos = await api.get<any[]>(`/prestamos?empresa=${empresa}`)
    return todos
      .filter((p) =>
        String(p.prestamoNro ?? '').includes(texto.trim()) ||
        String(p.clienteCedula ?? '').includes(texto.trim()) ||
        String(p.clienteNombre ?? '').toLowerCase().includes(t),
      )
      .slice(0, 10)
  }

  const renderPrestamo = (p: any) => ({
    titulo: `${p.clienteNombre} — Préstamo #${p.prestamoNro}`,
    subtitulo: `Ruta: ${p.ruta ?? '—'}`,
    detalle: [`Saldo: ${fmtMoneda(p.prestamoSaldo)}`, `Cuota: ${fmtMoneda(p.prestamoCuota)}`],
  })

  // Seleccionar préstamo → precarga el preview del abono (valor sugerido CON
  // pico + próximo cobro calculado — PrestamosController.cs AbonoPreview,
  // réplica abonosgeneral_level_detail.cs:238 + asignaproximocobro.cs:101-143).
  // Si el preview falla (préstamo sin datos) se conserva el comportamiento
  // actual: la cuota del préstamo como sugerido y su próximo cobro.
  const seleccionarPrestamo = async (p: any) => {
    setPrestamoSel(p)
    try {
      const prev = await api.get<any>(`/prestamos/${p.prestamoNro}/abono-preview?empresa=${empresa}`)
      const cobro = prev?.proximoCobro ? String(prev.proximoCobro).slice(0, 10) : ''
      setFormAgil((f) => ({
        ...f,
        valor: prev?.valorSugerido != null ? String(prev.valorSugerido) : f.valor,
        proximoCobro: cobro && !cobro.startsWith('0001') ? cobro : f.proximoCobro,
      }))
      setPreviewAgil(prev ?? null)
    } catch {
      setPreviewAgil(null)
    }
  }

  const limpiarPrestamo = () => {
    setPrestamoSel(null)
    setPreviewAgil(null)
  }

  const resumenPrestamo = (p: any) => ({
    titulo: `${p.clienteNombre} — Préstamo #${p.prestamoNro}`,
    lineas: [
      `Saldo: ${fmtMoneda(p.prestamoSaldo)}`,
      `Cuota: ${fmtMoneda(p.prestamoCuota)}`,
      `Ruta: ${p.ruta ?? '—'}`,
    ],
  })

  // Ticket PDF real (QuestPDF, réplica aticket.cs) — unificado al helper
  // global api.abrirPdf (pestaña nueva, patrón anti-popup; antes repetía el
  // blob + window.open aquí mismo). Sin `saldo` en la URL el backend imprime
  // el saldo (default true, igual que la vista).
  const verTicket = async (fila: any) => {
    try {
      await api.abrirPdf(`/abonos/${fila.abonoNro}/ticket.pdf?empresa=${empresa}`)
    } catch (e: any) {
      setError(e.message ?? 'No se pudo abrir el ticket')
    }
  }
  const contactar = (fila: any) => {
    const celular = String(fila.clienteCelular ?? '').trim()
    if (!celular) {
      setAviso('Cliente sin celular')
      return
    }
    window.open(`https://wa.me/${numeroWhatsApp(celular)}?text=${encodeURIComponent('Hola, te escribo desde SiprinIA')}`, '_blank')
  }
  // [RGM] 2026-10-01 (faltantes-multitema C): iconos en las acciones de fila
  // — Ticket (cilFile) y Contactar (cilPhone), AMBAS fijas al final de la
  // fila (inline-end). Se corrige también el typo del label ("Conctactar" →
  // "Contactar", mismo texto que PrestamosPage/AtrasosPage).
  const rowActions: WwRowAction[] = [
    { label: 'Ticket', icon: 'cilFile', placement: 'inline-end', onClick: verTicket },
    { label: 'Contactar', icon: 'cilPhone', placement: 'inline-end', onClick: contactar },
  ]

  const [totalReg, setTotalReg] = useState(rows.length)
  // Hint del próximo cobro del préstamo elegido (dd/mm/yyyy).
  const hintProximoCobro = prestamoSel ? fmtFechaValida(prestamoSel.prestamoProximoCobro) : ''
  return (
    <>
      <CRow className="mb-4">
        <CCol>
          <h4>Abono</h4>
          <small className="text-body-secondary">{totalReg} {totalReg === 1 ? 'registro' : 'registros'}</small>
        </CCol>
      </CRow>
      {error && <CAlert color="danger">{error}</CAlert>}
      {exitoAgil && <CAlert color="success" dismissible onClose={() => setExitoAgil('')}>{exitoAgil}</CAlert>}
      {aviso && <CAlert color="warning" dismissible onClose={() => setAviso('')}>{aviso}</CAlert>}

      <CTabs defaultActiveItemKey="agil">
        <CTabList variant="tabs">
          <CTab itemKey="agil">Abono Ágil</CTab>
          <CTab itemKey="historial">Historial</CTab>
        </CTabList>

        {/* Tab Abono Ágil — formulario de registro rediseñado (diseño RGM).
            Reusa la búsqueda existente (WwSearchField + /prestamos) y la misma
            lógica de alta; solo cambia la presentación. */}
        <CTabPanel itemKey="agil" className="pt-3">
          <CCard>
            <CCardHeader className="bg-body-tertiary fw-semibold">Ingresar Abono</CCardHeader>
            <CCardBody>
              <CForm onSubmit={registrarAgil}>
                <CRow className="g-3">
                  <CCol md={8}>
                    <CFormLabel>Cliente o préstamo</CFormLabel>
                    <WwSearchField
                      placeholder="Cédula, nombre o N.º préstamo"
                      onBuscar={buscarPrestamos}
                      renderItem={renderPrestamo}
                      onSeleccionar={seleccionarPrestamo}
                      limpiar
                      onLimpiar={limpiarPrestamo}
                      seleccionado={prestamoSel !== null}
                      resumen={prestamoSel ? resumenPrestamo(prestamoSel) : undefined}
                    />
                  </CCol>
                  <CCol md={4}>
                    <CFormLabel>Saldo actual</CFormLabel>
                    <CFormInput value={prestamoSel ? fmtMoneda(prestamoSel.prestamoSaldo) : ''} readOnly />
                    {/* [RGM] 2026-09-24 (pedido Rafael): el PICO en la parte
                        superior (solo si tiene — > 0). */}
                    {prestamoSel && Number(prestamoSel.saldoPico ?? 0) > 0 && (
                      <small className="text-body-secondary">Pico: {fmtMoneda(prestamoSel.saldoPico)}</small>
                    )}
                  </CCol>
                  <CCol md={6}>
                    <CFormLabel>Monto</CFormLabel>
                    {/* [RGM] 2026-10-02 (moneda global, pedido Rafael): prefijo
                        de la moneda configurada + formato de miles en vivo
                        (mismo patrón del form de Préstamo). */}
                    <CInputGroup>
                      <CInputGroupText>{simboloMoneda()}</CInputGroupText>
                      <NumericFormat
                        className="form-control"
                        value={formAgil.valor}
                        onValueChange={(v) => setFormAgil({ ...formAgil, valor: v.value })}
                        placeholder="0.00"
                        thousandSeparator=","
                        decimalSeparator="."
                        decimalScale={2}
                        fixedDecimalScale
                        allowNegative={false}
                        inputMode="decimal"
                      />
                    </CInputGroup>
                    {prestamoSel && (
                      /* Sugerido = valor del preview (con pico); si el preview no
                         está disponible, fallback = cuota del préstamo.
                         [RGM] 2026-09-24 (pedido Rafael): cuando el sugerido
                         viene del PICO se explica: "por pico de 10" — la
                         fórmula GX es (Cuota × 2) − Pico. */
                      <small className="text-body-secondary">
                        Sugerido: {fmtMoneda(previewAgil?.valorSugerido ?? prestamoSel.prestamoCuota)}
                        {Number(prestamoSel.saldoPico ?? 0) > 0
                          ? ` — por pico de ${fmtMoneda(prestamoSel.saldoPico)}`
                          : ''}
                      </small>
                    )}
                  </CCol>
                  <CCol md={6}>
                    <CFormLabel>Método de Pago</CFormLabel>
                    {/* 1=Efectivo, 2=Transferencia → AbonoLibreValor3 (semántica RGM) */}
                    <CFormSelect value={formAgil.metodoPago} onChange={(e) => setFormAgil({ ...formAgil, metodoPago: e.target.value })}>
                      <option value="1">Efectivo</option>
                      <option value="2">Transferencia</option>
                    </CFormSelect>
                  </CCol>
                  <CCol md={6}>
                    <CFormLabel>Fecha de Pago</CFormLabel>
                    <CFormInput type="date" value={formAgil.fecha} onChange={(e) => setFormAgil({ ...formAgil, fecha: e.target.value })} />
                  </CCol>
                  <CCol md={6}>
                    <CFormLabel>Observaciones</CFormLabel>
                    {/* AbonoLibreCaracter1 del abono (columna Observación del grid) */}
                    <CFormInput value={formAgil.observaciones} onChange={(e) => setFormAgil({ ...formAgil, observaciones: e.target.value })} onBlur={(e) => setFormAgil({ ...formAgil, observaciones: capitalizarPalabras(e.target.value) })} />
                  </CCol>
                  <CCol md={6}>
                    <CFormLabel>Próximo cobro</CFormLabel>
                    <CFormInput type="date" value={formAgil.proximoCobro} onChange={(e) => setFormAgil({ ...formAgil, proximoCobro: e.target.value })} />
                    {hintProximoCobro && (
                      <small className="text-body-secondary">Próximo cobro actual: {hintProximoCobro}</small>
                    )}
                  </CCol>
                  <CCol xs={12} className="d-flex justify-content-end">
                    <CButton color="primary" type="submit" disabled={!prestamoSel || !formAgil.valor || guardandoAgil}>
                      {guardandoAgil ? (
                        <><CSpinner size="sm" className="me-1" />Registrando…</>
                      ) : (
                        'Registrar Abono'
                      )}
                    </CButton>
                  </CCol>
                </CRow>
              </CForm>
            </CCardBody>
          </CCard>
        </CTabPanel>

        {/* Tab Historial — grilla WwGrid EXISTENTE (abonoww). El ALTA vive en el
            tab Abono Ágil, por eso el grid ya no muestra el botón Agregar. */}
        <CTabPanel itemKey="historial" className="pt-3">
          <WwGrid
            filters={
              <WwFilterBar
                selects={[
                  { key: 'tipo', label: 'Todos los Tipos', opciones: tiposAbono, valor: filtroTipo, onChange: setFiltroTipo },
                  { key: 'cobrador', label: 'Todos los Cobradores', opciones: cobradoresAbono, valor: filtroCobrador, onChange: setFiltroCobrador },
                ]}
                chips={chips}
                onQuitar={quitarFiltro}
                onLimpiar={limpiarFiltros}
              />
            }
            title="Abono"
            columns={columnas}
            rows={visibleRows}
            onTotalChange={setTotalReg}
            loading={cargando}
            onEditar={abrirEditar}
            onEliminar={eliminar}
            rowActions={rowActions}
            busquedaPlaceholder="Buscar por abono, préstamo o cliente..."
            onRowLink={(colKey, row) => {
              if (colKey === 'abonoNro') navigate(`/abonos/${row.abonoNro}`)
            }}
          />
        </CTabPanel>
      </CTabs>

      {/* Modal de EDICIÓN compartido (abonoww UPD). [RGM] 2026-10-03: se
          eliminó el modal viejo de la grilla — ahora es el MISMO componente
          que usa el detalle (AbonoViewPage): mismos campos, mismas
          validaciones y mismo PUT /api/abonos/{nro}. */}
      <ModificarAbonoModal
        abono={editando}
        empresa={empresa}
        visible={editando !== null}
        onClose={() => setEditando(null)}
        onSaved={() => { setEditando(null); cargar() }}
      />
    </>
  )
}

export default AbonosPage